import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { DataGrid } from '@/components/tables/DataGrid';
import type { DataGridColumn } from '@/components/tables/DataGrid';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { useAuth } from '@/auth/AuthContext';
import { tokenStorage } from '@/auth/tokenStorage';
import { accountSessionApi } from '@/api/services/accountSession';
import { ApiError } from '@/api/client/abpError';
import { tenantsApi } from '@/api/services/tenants';
import type { FinkitTenantDto } from '@/api/types/tenants';
import { TenantPermissions } from './permissions';
import { TenantFormModal } from './TenantFormModal';
import type { TenantCreateFormValues } from './TenantFormModal';
import { ResetPasswordModal } from './ResetPasswordModal';
import type { ResetPasswordFormValues } from './ResetPasswordModal';
import { useCreateTenant, useDeleteTenant, useResetAdminPassword } from './api';

type ModalState = { mode: 'create' } | null;

export function TenantsPage() {
  const can = usePermission();
  const canCreate = can(TenantPermissions.create);
  const canUpdate = can(TenantPermissions.update);
  const canDelete = can(TenantPermissions.delete);

  const navigate = useNavigate();
  const toast = useToast();
  const { completeLogin } = useAuth();
  const createMut = useCreateTenant();
  const deleteMut = useDeleteTenant();
  const resetMut = useResetAdminPassword();

  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<FinkitTenantDto | null>(null);
  const [resetTarget, setResetTarget] = useState<FinkitTenantDto | null>(null);
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);

  const table = useServerDataTable<FinkitTenantDto>({
    queryKey: ['tenants', 'list'],
    fetcher: (req) => tenantsApi.getListForDataTable(req),
    pageSize: 5,
  });

  function reportError(err: unknown, fallback: string) {
    toast.error(err instanceof ApiError ? err.message : fallback);
  }

  // Host "Login as client admin": park the host session (so "Return to my account"
  // restores it), swap to the tenant-admin bearer, then load the app. Mirrors the
  // tenant-side Identity → Users "Login as".
  async function impersonate(tenant: FinkitTenantDto) {
    const current = tokenStorage.load();
    if (!current) {
      reportError(null, 'Your session is no longer valid. Please sign in again.');
      return;
    }
    setImpersonatingId(tenant.id);
    try {
      tokenStorage.stashImpersonator(current);
      const result = await accountSessionApi.impersonateTenant(tenant.id);
      if (result.error || !result.accessToken) {
        tokenStorage.clearImpersonatorStash();
        reportError(null, result.errorDescription || 'Unable to log in as this client admin.');
        return;
      }
      await completeLogin();
      navigate('/');
    } catch (err) {
      tokenStorage.clearImpersonatorStash();
      reportError(err, 'Unable to log in as this client admin.');
    } finally {
      setImpersonatingId(null);
    }
  }

  function goToEdit(row: FinkitTenantDto) {
    // Legacy: setup-pending -> 3-step Setup wizard, otherwise -> Edit page.
    const dest = row.isSetupPending ? 'setup' : 'edit';
    navigate(`/host/tenants/${encodeURIComponent(row.id)}/${dest}`);
  }

  function handleCreate(values: TenantCreateFormValues) {
    const connectionStrings = values.connectionStrings.filter((c) => c.name.trim() || c.value.trim());
    createMut.mutate(
      {
        name: values.name.trim(),
        adminEmailAddress: values.adminEmailAddress.trim(),
        adminPassword: values.adminPassword,
        connectionStrings: connectionStrings.length ? connectionStrings : undefined,
      },
      {
        onSuccess: () => {
          toast.success('Client created.');
          setModal(null);
        },
        onError: (err) => reportError(err, 'Failed to create the client.'),
      },
    );
  }

  function handleResetPassword(values: ResetPasswordFormValues) {
    if (!resetTarget) return;
    resetMut.mutate(
      { id: resetTarget.id, input: { oldPassword: values.oldPassword, newPassword: values.newPassword } },
      {
        onSuccess: () => {
          toast.success('Password updated.');
          setResetTarget(null);
        },
        onError: (err) => reportError(err, 'Failed to update password.'),
      },
    );
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    deleteMut.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success('Successfully deleted.');
        setDeleteTarget(null);
      },
      onError: (err) => {
        reportError(err, 'Failed to delete the client.');
        setDeleteTarget(null);
      },
    });
  }

  const columns: DataGridColumn<FinkitTenantDto>[] = [
    {
      key: 'email',
      header: 'Admin Email',
      sortable: true,
      filterable: true,
      className: 'text-start',
      cell: (r) => {
        const email = r.email ?? '';
        if (!email || r.isSetupPending) return email;
        return (
          <button
            type="button"
            className="btn-link p-0 border-0 bg-transparent text-start"
            title="Log in as client admin"
            disabled={impersonatingId !== null}
            onClick={() => void impersonate(r)}
          >
            {email}
          </button>
        );
      },
    },
    {
      key: 'name',
      header: 'Client Name',
      sortable: true,
      filterable: true,
      className: 'text-start',
      cell: (r) => r.name,
    },
    {
      key: 'isSetupPending',
      header: 'Status',
      className: 'text-start',
      cell: (r) =>
        r.isSetupPending ? (
          <span className="badge bg-warning text-dark">Pending</span>
        ) : (
          <span className="badge bg-success">Ready</span>
        ),
    },
  ];

  const showRowActions = canUpdate || canDelete;

  return (
    <div>
      <PageHeader title="Clients" breadcrumb={['Clients']} />

      <div className="card">
        <div className="card-body">
          <DataGrid
            controller={table}
            columns={columns}
            rowKey={(r) => r.id}
            emptyMessage="No clients found."
            toolbarEnd={
              canCreate && (
                <button
                  type="button"
                  className="btn btn-primary"
                  title="Create new client"
                  onClick={() => setModal({ mode: 'create' })}
                >
                  <i className="fa fa-plus me-1" />
                  Add Client
                </button>
              )
            }
            rowActions={
              showRowActions
                ? (row) => (
                    <div className="d-inline-flex gap-1 justify-content-center">
                      {canUpdate && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          title={row.isSetupPending ? 'Continue Setup' : 'Edit'}
                          onClick={() => goToEdit(row)}
                        >
                          <i className="fa fa-edit" />
                        </button>
                      )}
                      {canUpdate && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          title="Change Admin Password"
                          onClick={() => setResetTarget(row)}
                        >
                          <i className="fa fa-key" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          title="Delete"
                          onClick={() => setDeleteTarget(row)}
                        >
                          <i className="fa fa-trash" />
                        </button>
                      )}
                    </div>
                  )
                : undefined
            }
          />
        </div>
      </div>

      {modal?.mode === 'create' && (
        <TenantFormModal
          show
          mode="create"
          busy={createMut.isPending}
          onSubmit={handleCreate}
          onClose={() => setModal(null)}
        />
      )}

      {resetTarget && (
        <ResetPasswordModal
          show
          tenantName={resetTarget.name}
          busy={resetMut.isPending}
          onSubmit={handleResetPassword}
          onClose={() => setResetTarget(null)}
        />
      )}

      <ConfirmDialog
        show={deleteTarget !== null}
        title="Delete client"
        message={`Are you sure you want to delete the client "${deleteTarget?.name ?? ''}"? This cannot be undone.`}
        confirmLabel="Delete"
        busy={deleteMut.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
